import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle, RefreshCw, ArrowRight, XCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PaykuService } from '../services/paykuService';
import { toast } from 'react-hot-toast';
import PaymentErrorModal from '../components/PaymentErrorModal';
import { TransactionVerifier } from '../services/transactionVerifier';
import { PaykuValidator } from '../services/paykuValidator';

const PaykuReturn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'manual'>('loading');
  const [message, setMessage] = useState<string>('Verificando el estado de tu pago...');
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = useState<number>(5);
  
  // Estado para el modal de error de pago
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [paymentErrorDetails, setPaymentErrorDetails] = useState<{
    transactionId: string;
    errorType: string;
    message: string;
    details?: any;
  } | null>(null);

  useEffect(() => {
    const checkPayment = async () => {
      try {
        setStatus('loading');
        
        // Obtener parámetros de la URL
        const params = new URLSearchParams(window.location.search);
        const urlId = params.get('id');
        const txid = params.get('txid');
        
        console.log('[PAYKU-RETURN] Parámetros URL:', { urlId, txid });
        
        // Obtener información de la transacción pendiente de sessionStorage y localStorage
        const pendingTransactionStr = sessionStorage.getItem('pendingTransaction') || localStorage.getItem('pendingTransaction');
        const pendingTransaction = pendingTransactionStr ? JSON.parse(pendingTransactionStr) : null;
        
        // También verificar en payku_transaction
        const paykuTransactionStr = sessionStorage.getItem('payku_transaction') || localStorage.getItem('payku_transaction');
        const paykuTransaction = paykuTransactionStr ? JSON.parse(paykuTransactionStr) : null;
        
        console.log('[PAYKU-RETURN] Transacción pendiente:', pendingTransaction);
        console.log('[PAYKU-RETURN] Transacción Payku:', paykuTransaction);
        
        // Determinar el ID de transacción a usar (priorizar URL)
        let transactionId = urlId || txid;
        
        // Si no hay ID en la URL, intentar obtenerlo del storage
        if (!transactionId) {
          if (pendingTransaction && pendingTransaction.id) {
            transactionId = pendingTransaction.id;
          } else if (paykuTransaction && paykuTransaction.id) {
            transactionId = paykuTransaction.id;
          }
        }
        
        if (!transactionId) {
          console.error('[PAYKU-RETURN] No se pudo determinar el ID de transacción');
          setStatus('error');
          setMessage('No se pudo identificar la transacción. Por favor, contacta a soporte.');
          return;
        }
        
        console.log('[PAYKU-RETURN] Verificando transacción con ID:', transactionId);
        setTransactionId(transactionId);
        
        // VERIFICACIÓN CRÍTICA: Verificar primero si la transacción fue cancelada
        const cancellationCheck = await TransactionVerifier.verifyTransactionWasCancelled(transactionId);
        
        if (cancellationCheck.cancelled) {
          console.error(`[PAYKU-RETURN] Transacción ${transactionId} está marcada como cancelada. Mostrando error.`);
          
          // Asegurar que esté marcada como cancelada en la base de datos
          await TransactionVerifier.markTransactionAsCancelled(
            transactionId, 
            `Detectado en página de retorno desde fuente: ${cancellationCheck.source}`
          );
          
          // Mostrar modal de error
          setPaymentErrorDetails({
            transactionId,
            errorType: 'PAYMENT_CANCELLED',
            message: 'El pago fue cancelado pero estás en la página de retorno de pago.'
          });
          
          setShowErrorModal(true);
          setStatus('error');
          return;
        }
        
        // Si no está cancelada, continuar con la verificación estricta usando PaykuValidator
        await verifyPaykuPaymentStrict(transactionId);
      } catch (error) {
        console.error('[PAYKU-RETURN] Error al verificar el pago:', error);
        setStatus('error');
        setMessage('Ocurrió un error al verificar el pago. Por favor, intenta nuevamente.');
      }
    };
    
    checkPayment();
  }, []);

  // NUEVO: Verificación estricta con PaykuValidator
  const verifyPaykuPaymentStrict = async (transactionId: string) => {
    try {
      console.log('[PAYKU-RETURN] Iniciando verificación estricta con PaykuValidator para ID:', transactionId);
      
      // Usar el nuevo servicio de validación
      const result = await PaykuValidator.processPayment(transactionId);
      
      console.log('[PAYKU-RETURN] Resultado de verificación estricta:', result);
      
      if (result.success) {
        // Pago verificado exitosamente
        setStatus('success');
        setMessage(result.message);
        
        // Si hay detalles de la transacción, mostrarlos
        if (result.details) {
          setTransactionDetails({
            vcoinsAmount: result.coinsAdded || result.details.transaction?.vcoins_amount,
            packageName: result.details.transaction?.metadata?.package_name || 'Paquete de VCoins'
          });
        }
        
        // Emitir evento para actualizar el estado de la aplicación
        const event = new CustomEvent('payment-success', { 
          detail: { 
            transactionId,
            timestamp: Date.now() 
          } 
        });
        window.dispatchEvent(event);
        
        // Disparar evento para actualizar las monedas en el header
        window.dispatchEvent(new Event('coinsUpdated'));
        
        // Limpiar datos de transacción pendiente
        try {
          localStorage.removeItem('pendingTransaction');
          sessionStorage.removeItem('pendingTransaction');
          localStorage.removeItem('payku_transaction');
          sessionStorage.removeItem('payku_transaction');
        } catch (e) {
          console.error('[PAYKU-RETURN] Error al limpiar datos de transacción:', e);
        }
        
        startRedirectCountdown();
      } else {
        // El pago no fue exitoso
        console.error('[PAYKU-RETURN] Verificación de pago fallida:', result);
        
        // Si el pago fue cancelado, mostrar modal específico
        if (result.status === 'cancelled') {
          setPaymentErrorDetails({
            transactionId,
            errorType: 'PAYMENT_CANCELLED',
            message: 'El pago fue cancelado o rechazado en Payku.',
            details: result.details
          });
          
          setShowErrorModal(true);
        } else if (result.status === 'pending') {
          // Si está pendiente, mostrar mensaje específico
          setStatus('error');
          setMessage('El pago está pendiente de confirmación. Por favor, intenta verificar nuevamente en unos minutos.');
        } else {
          // Otro tipo de error
          setStatus('error');
          setMessage(result.message || 'No se pudo verificar el pago. Por favor, contacta a soporte.');
        }
      }
    } catch (error) {
      console.error('[PAYKU-RETURN] Error en verifyPaykuPaymentStrict:', error);
      setStatus('error');
      setMessage('Ocurrió un error al verificar el pago. Por favor, contacta a soporte.');
    }
  };

  // Iniciar cuenta regresiva para redirección
  const startRedirectCountdown = () => {
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/account');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setCountdownInterval(interval);
  };

  // Verificar manualmente
  const handleManualVerify = () => {
    if (!transactionId || loading) return;
    
    setLoading(true);
    setStatus('loading');
    setMessage('Verificando el estado de tu pago...');
    
    verifyPaykuPaymentStrict(transactionId)
      .finally(() => setLoading(false));
  };

  // Ir a la cuenta
  const goToAccount = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    navigate('/account');
  };

  // Manejadores para el modal de error
  const handleRetryPayment = () => {
    // Cerrar el modal
    setShowErrorModal(false);
    
    // Redirigir a la página de compra de monedas
    navigate('/coins');
  };
  
  const handleGoBack = () => {
    // Cerrar el modal
    setShowErrorModal(false);
    
    // Redirigir al dashboard
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black pt-12 pb-20">
      {/* Modal de error de pago */}
      <PaymentErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        transactionId={paymentErrorDetails?.transactionId}
        onRetry={handleRetryPayment}
        onGoBack={handleGoBack}
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 sm:p-8 shadow-lg">
          <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-6 text-center">
            Verificación de Pago
          </h1>
          
          <div className="flex flex-col items-center justify-center">
            {status === 'loading' ? (
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mb-4 mx-auto"></div>
                <h2 className="text-xl font-semibold text-white mb-2">Verificando pago</h2>
                <p className="text-gray-300">{message}</p>
                <p className="text-sm text-gray-400 mt-4">Esto puede tomar unos momentos. Por favor, no cierres esta ventana.</p>
              </div>
            ) : status === 'success' ? (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-4 mx-auto">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">¡Pago Exitoso!</h2>
                <p className="text-gray-300 mb-4">{message}</p>
                
                {transactionDetails && (
                  <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-500/30 max-w-md mx-auto mb-6">
                    <h3 className="text-lg font-medium text-purple-300 mb-2">Resumen de la transacción</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-400">Paquete:</div>
                      <div className="text-white font-medium">{transactionDetails.packageName || 'Pack de VCoins'}</div>
                      
                      <div className="text-gray-400">VCoins adquiridos:</div>
                      <div className="text-yellow-400 font-bold">{transactionDetails.vcoinsAmount}</div>
                    </div>
                  </div>
                )}
                
                <p className="text-gray-400 mb-6">
                  Serás redirigido a tu cuenta en <span className="text-white font-bold">{countdown}</span> segundos
                </p>
                
                <button
                  onClick={goToAccount}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all transform hover:scale-[1.02] flex items-center gap-2 justify-center"
                >
                  <span>Ir a mi cuenta ahora</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-4 mx-auto">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Verificación fallida</h2>
                <p className="text-gray-300 mb-6">{message}</p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={handleManualVerify}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Intentar verificar nuevamente</span>
                  </button>
                  
                  <button
                    onClick={goToAccount}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
                  >
                    <span>Volver a mi cuenta</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                
                <p className="text-sm text-gray-400 mt-6">
                  Si realizaste un pago y no se ha reflejado en tu cuenta, por favor contacta a soporte con el ID de transacción: <span className="text-white font-mono">{transactionId || 'No disponible'}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaykuReturn; 